import EngineeringOutlined from '@mui/icons-material/EngineeringOutlined';
import MonetizationOnOutlined from '@mui/icons-material/MonetizationOnOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';

import SignaturesViewer from '~components/Signatures/SignaturesViewer';
import { useDriverDays } from '~hooks/useDriverDays/useDriverDays';
import { useJob } from '~hooks/useJob/useJob';
import { DriverDaySignature, JobSignature } from '~hooks/useSignatures/models';
import useApprovalCardShadow from '~pages/Approvals/shared/useApprovalCardShadow';
import { Nullable } from '~types/Nullable';
import { dateFormat } from '~utils/dateTime';

const iconsByRole = {
  billing: MonetizationOnOutlined,
  field: EngineeringOutlined,
} as const;

interface CategoryApprovalsCardProps {
  billingApprovedAt: Nullable<Dayjs>;
  driverDayId?: string;
  fieldApprovedAt: Nullable<Dayjs>;
  jobId?: string;
}

const CategoryApprovalsCard = ({
  billingApprovedAt,
  driverDayId,
  fieldApprovedAt,
  jobId,
}: CategoryApprovalsCardProps) => {
  const theme = useTheme();
  const cardShadow = useApprovalCardShadow();
  const { getDriverDaySignatures } = useDriverDays();
  const { getJobSignatures } = useJob();
  const [signatures, setSignatures] = useState<(DriverDaySignature | JobSignature)[]>([]);

  useEffect(() => {
    if (driverDayId) {
      getDriverDaySignatures(driverDayId).then((signatures) => {
        setSignatures(signatures);
      });
    } else if (jobId) {
      getJobSignatures(jobId).then((signatures) => {
        setSignatures(signatures);
      });
    }
  }, [driverDayId, jobId]);

  const approvals = useMemo(() => {
    return [
      {
        approvedAt: billingApprovedAt,
        approvedBy: 'billing',
        isApproved: !!billingApprovedAt,
      },
      {
        approvedAt: fieldApprovedAt,
        approvedBy: 'field',
        isApproved: !!fieldApprovedAt,
      },
    ];
  }, [billingApprovedAt, fieldApprovedAt]);

  return (
    <Box
      sx={{
        bgcolor: theme.semantic.surfaceRaised,
        border: `1px solid ${theme.brandV2.colors.treadGray7}`,
        borderRadius: 2,
        boxShadow: cardShadow,
        display: 'flex',
        gap: 2,
        p: 1.5,
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
          {t('approvals.driver_day.shift_approved_by')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {approvals.map((approval) => {
            const Icon = iconsByRole[approval.approvedBy as keyof typeof iconsByRole];
            return (
              <Box
                key={approval.approvedBy}
                sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
              >
                <Box
                  sx={{
                    flex: 0,
                    minWidth: '70px',
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                  }}
                >
                  <Icon
                    sx={{
                      color: approval.isApproved
                        ? theme.brandV2.colors.treadGreen
                        : theme.brandV2.colors.treadGray7,
                      fontSize: '16px',
                      height: '16px',
                      width: '16px',
                    }}
                  />

                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: theme.brandV2.colors.treadBlack,
                    }}
                  >
                    {t(`approvals.driver_day.${approval.approvedBy}`)}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {approval.approvedAt ? (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 'inherit',
                          color: theme.brandV2.colors.treadGray3,
                        }}
                      >
                        {dateFormat(approval.approvedAt, 'h:mm A ddd MMM-DD-YYYY')}
                      </Typography>
                    ) : null}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {signatures.length > 0 && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
            {t('ticket_drawer_v2.digital_ticket.signatures')}
          </Typography>

          <SignaturesViewer signatures={signatures} sx={{ flexDirection: 'column' }} />
        </Box>
      )}
    </Box>
  );
};

export default CategoryApprovalsCard;
